import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, HeartPulse, ShieldCheck } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import api from '@/lib/api';

export default function MyProfile() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/portal/profile').then((r) => setForm(r.data)).catch(() => toast.error('Erro ao carregar perfil')).finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        nome: form.nome, data_nascimento: form.data_nascimento, email: form.email, telefone: form.telefone, rg: form.rg, cpf: form.cpf,
        tipo_sanguineo: form.tipo_sanguineo, alergias: form.alergias,
        remedios_continuos: form.remedios_continuos, obs: form.obs,
      };
      const r = await api.put('/portal/profile', payload);
      setForm(r.data);
      toast.success('Dados atualizados com sucesso');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  if (loading) return <p className="text-muted-foreground">Carregando...</p>;
  if (!form) return <p className="text-muted-foreground">Cadastro não encontrado.</p>;

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <h2 className="font-display text-2xl font-semibold">Meu Perfil</h2>
        <p className="text-sm text-muted-foreground">Mantenha seus dados atualizados para uma viagem segura.</p>
      </div>

      <form onSubmit={save}>
        <Card className="p-6 space-y-5" data-testid="portal-profile-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Nome completo</Label><Input value={form.nome || ''} onChange={set('nome')} className="mt-1.5" data-testid="profile-nome" /></div>
            <div><Label>Data de nascimento</Label><Input type="date" value={form.data_nascimento || ''} onChange={set('data_nascimento')} className="mt-1.5" data-testid="profile-nascimento" /></div>
            <div><Label>CPF</Label><Input value={form.cpf || ''} onChange={set('cpf')} className="mt-1.5" /></div>
            <div><Label>RG</Label><Input value={form.rg || ''} onChange={set('rg')} className="mt-1.5" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email || ''} onChange={set('email')} className="mt-1.5" /></div>
            <div><Label>Telefone</Label><Input value={form.telefone || ''} onChange={set('telefone')} className="mt-1.5" data-testid="profile-telefone" /></div>
          </div>

          <Accordion type="single" collapsible className="border rounded-lg px-4">
            <AccordionItem value="saude" className="border-0">
              <AccordionTrigger className="text-sm"><span className="flex items-center gap-2"><HeartPulse className="h-4 w-4 text-primary" /> Informações de saúde</span></AccordionTrigger>
              <AccordionContent className="space-y-4 pt-2">
                <p className="text-xs text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Usadas apenas para emergências, conforme a LGPD.</p>
                <div><Label>Tipo sanguíneo</Label><Input value={form.tipo_sanguineo || ''} onChange={set('tipo_sanguineo')} placeholder="Ex: O+" className="mt-1.5" /></div>
                <div><Label>Alergias</Label><Textarea value={form.alergias || ''} onChange={set('alergias')} rows={2} className="mt-1.5" /></div>
                <div><Label>Remédios contínuos</Label><Textarea value={form.remedios_continuos || ''} onChange={set('remedios_continuos')} rows={2} className="mt-1.5" /></div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div><Label>Observações</Label><Textarea value={form.obs || ''} onChange={set('obs')} rows={2} className="mt-1.5" /></div>

          <div className="flex justify-end">
            <Button type="submit" disabled={saving} data-testid="portal-profile-save-button">{saving ? 'Salvando...' : <><Save className="h-4 w-4 mr-2" /> Salvar alterações</>}</Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
